import React, { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { CATEGORY_OPTIONS as CATEGORIES, getCodeTemplateApplyOptions, getCodeTemplatePreset } from '../templates/categoryLanding/registry'
import MenuGuideButton from '../components/MenuGuideButton'

function getCreateWritingItems(selectedCategory, selectedTemplate) {
  return [
    {
      id: 'hero',
      label: '상단 제목·설명',
      description: `${selectedCategory?.label || '선택한 카테고리'}에 맞는 기본 문구를 확인하고 필요하면 수정합니다.`,
    },
    {
      id: 'content',
      label: '본문 섹션',
      description: selectedTemplate?.label
        ? `${selectedTemplate.label} 구성에 맞춰 필요한 설명을 채웁니다.`
        : '선택한 템플릿 구성에 맞춰 필요한 설명을 채웁니다.',
    },
    {
      id: 'form',
      label: '상담 신청 폼',
      description: '문의받을 항목과 폼 제목을 확인합니다.',
    },
    {
      id: 'ad-review',
      label: '심의필 문구',
      description: '저장할 때 광고 심의필 문구를 확인합니다.',
    },
  ]
}

function getTemplatePresetKey(templateId) {
  const raw = String(templateId || 'default')
  if (raw === 'default') return 'default'
  return raw.startsWith('preset:') ? raw.slice('preset:'.length) : raw
}

function getRequiredInfoPreview(selectedCategory, selectedTemplate) {
  const categoryLabel = selectedCategory?.label || '홍보용페이지'
  const preset = selectedTemplate?.source === 'code'
    ? getCodeTemplatePreset(selectedCategory?.code, selectedTemplate.presetKey || getTemplatePresetKey(selectedTemplate.id))
    : selectedTemplate?.template

  return [
    {
      id: 'heroTitle',
      label: '대표 제목',
      value: preset?.heroTitle || `${categoryLabel}, 핵심만 쉽게 안내해드립니다`,
    },
    {
      id: 'heroSubtitle',
      label: '대표 설명',
      value: preset?.heroSubtitle || '필요한 정보를 빠르게 확인하고 부담 없이 상담을 신청해보세요.',
    },
    {
      id: 'formTitle',
      label: '상담 폼 제목',
      value: preset?.formTitle || `${categoryLabel} 상담 신청`,
    },
    {
      id: 'adReviewNotice',
      label: '심의필 문구',
      value: '게시 전 실제 심의필 문구를 입력합니다.',
    },
  ]
}

function CategoryLandingCreateContent({
  categories,
  categoryCode,
  disabledCategoryCodes = [],
  templatesByCategory,
  templateValue,
  creating,
  onCategoryChange,
  onTemplateChange,
  onCreate,
  onClose,
  onCancel,
  title = '홍보용 페이지 만들기',
  description,
  showCloseButton = true,
  showCancelButton = true,
  className = '',
  resetKey,
}) {
  const [stepIndex, setStepIndex] = React.useState(0)
  const disabledCategorySet = React.useMemo(() => new Set(disabledCategoryCodes), [disabledCategoryCodes])
  const selectableCategories = React.useMemo(
    () => categories.filter((cat) => !disabledCategorySet.has(cat.code)),
    [categories, disabledCategorySet]
  )
  const selectedCategory = selectableCategories.find((cat) => cat.code === categoryCode) || selectableCategories[0] || categories[0]
  const hasSelectableCategory = selectableCategories.length > 0
  const codeTemplateOptions = selectedCategory ? getCodeTemplateApplyOptions(selectedCategory.code) : []
  const savedTemplates = selectedCategory ? (templatesByCategory[selectedCategory.code] || []) : []
  const options = [
    ...codeTemplateOptions.map((option) => ({
      id: option.value,
      label: option.label,
      description: `${option.hintTitle || ''} ${option.hintDescription || ''}`.trim(),
      source: 'code',
      presetKey: option.presetKey,
    })),
    ...savedTemplates.map((template) => ({
      id: String(template.id),
      label: template.name || `저장 템플릿 ${template.id}`,
      description: template.description || '저장된 템플릿으로 시작합니다.',
      source: 'saved',
      template,
    })),
  ]
  const selectedTemplate = options.find((option) => option.id === templateValue) || options[0]
  const stepLabels = ['카테고리 선택', '템플릿 선택', '필수 정보', '작성 항목']
  const writingItems = getCreateWritingItems(selectedCategory, selectedTemplate)
  const requiredInfoItems = getRequiredInfoPreview(selectedCategory, selectedTemplate)

  React.useEffect(() => {
    setStepIndex(0)
  }, [resetKey])

  React.useEffect(() => {
    if (!hasSelectableCategory || !selectedCategory) return
    if (selectedCategory.code !== categoryCode) onCategoryChange?.(selectedCategory.code)
  }, [categoryCode, hasSelectableCategory, onCategoryChange, selectedCategory])

  React.useEffect(() => {
    if (!selectedTemplate) return
    if (selectedTemplate.id !== templateValue) onTemplateChange?.(selectedTemplate.id)
  }, [onTemplateChange, selectedTemplate, templateValue])

  if (!selectedCategory) return null

  return (
    <div className={`flex max-h-[calc(100vh-3rem)] w-full flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-black/10 sm:max-h-[92vh] ${className}`}>
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-lg font-black text-slate-950">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
          ) : null}
        </div>
        {showCloseButton && onClose ? (
          <button
            type="button"
            className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            onClick={onClose}
          >
            닫기
          </button>
        ) : null}
      </div>

      <div className="min-h-0 overflow-y-auto px-5 py-5">
        <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {stepLabels.map((label, index) => {
            const active = index === stepIndex
            const done = index < stepIndex
            return (
              <button
                key={label}
                type="button"
                className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-left text-xs font-bold transition ${
                  active
                    ? 'border-brand-navy bg-sky-50 text-brand-navy'
                    : done
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-400'
                }`}
                onClick={() => {
                  if (index <= stepIndex) setStepIndex(index)
                }}
                aria-current={active ? 'step' : undefined}
              >
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] ${
                  active
                    ? 'bg-brand-navy text-white'
                    : done
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-100 text-slate-400'
                }`}>
                  {index + 1}
                </span>
                <span className="min-w-0 truncate">{label}</span>
              </button>
            )
          })}
        </div>

        {stepIndex === 0 ? (
          <section>
            <div className="text-sm font-bold text-slate-900">어떤 카테고리의 랜딩 페이지를 만들까요?</div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {categories.map((cat) => {
                const disabled = disabledCategorySet.has(cat.code)
                const active = !disabled && cat.code === selectedCategory.code
                return (
                  <button
                    key={cat.code}
                    type="button"
                    disabled={disabled}
                    className={`rounded-2xl border px-4 py-3 text-left transition ${
                      active
                        ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                        : disabled
                          ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                    onClick={() => {
                      if (!disabled) onCategoryChange?.(cat.code)
                    }}
                  >
                    <span className="block text-sm font-extrabold">{cat.label}</span>
                    {disabled ? (
                      <span className="mt-1 block text-xs font-bold text-slate-400">이미 생성됨</span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </section>
        ) : stepIndex === 1 ? (
          <section>
            <div className="text-sm font-bold text-slate-900">{selectedCategory.label} 템플릿을 골라주세요</div>
            <div className="mt-3 grid gap-2">
              {options.map((option) => {
                const active = option.id === selectedTemplate?.id
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`rounded-2xl border px-4 py-3 text-left transition ${
                      active
                        ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                    onClick={() => onTemplateChange?.(option.id)}
                  >
                    <span className="block text-sm font-extrabold">{option.label}</span>
                    {option.description ? (
                      <span className="mt-1 block text-xs leading-5 text-slate-500">{option.description}</span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </section>
        ) : stepIndex === 2 ? (
          <section>
            <div className="text-sm font-bold text-slate-900">기본 문구가 먼저 들어갑니다</div>
            <div className="mt-3 grid gap-2">
              {requiredInfoItems.map((item) => (
                <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-xs font-black text-slate-500">{item.label}</div>
                  <div className="mt-1 whitespace-pre-line break-keep text-sm font-bold leading-6 text-slate-900">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                {selectedCategory.label}
              </span>
              {selectedTemplate?.label ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                  {selectedTemplate.label}
                </span>
              ) : null}
            </div>
          </section>
        ) : (
          <section>
            <div className="text-sm font-bold text-slate-900">이 순서로 작성합니다</div>
            <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white">
              {writingItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex gap-3 px-4 py-4 ${index === 0 ? '' : 'border-t border-slate-100'}`}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-black text-brand-navy">
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-slate-950">{item.label}</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{item.description}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                {selectedCategory.label}
              </span>
              {selectedTemplate?.label ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                  {selectedTemplate.label}
                </span>
              ) : null}
            </div>
          </section>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4">
        {showCancelButton && onCancel ? (
          <button
            type="button"
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={onCancel}
          >
            취소
          </button>
        ) : null}
        {stepIndex > 0 ? (
          <button
            type="button"
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => setStepIndex((idx) => Math.max(0, idx - 1))}
          >
            이전
          </button>
        ) : null}
        <button
          type="button"
          className="rounded-xl bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          disabled={creating || !selectedTemplate || !hasSelectableCategory}
          onClick={() => {
            if (!hasSelectableCategory) return
            if (stepIndex < stepLabels.length - 1) {
              setStepIndex((idx) => Math.min(stepLabels.length - 1, idx + 1))
              return
            }
            onCreate?.()
          }}
        >
          {stepIndex < stepLabels.length - 1 ? '다음' : creating ? '생성 중...' : '생성하기'}
        </button>
      </div>
    </div>
  )
}

function CategoryLandingCreateModal({
  open,
  onClose,
  ...props
}) {
  React.useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center overflow-hidden bg-slate-950/55 px-4 py-6">
      <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-2xl sm:max-h-[92vh]">
        <CategoryLandingCreateContent
          {...props}
          onClose={onClose}
          onCancel={onClose}
          resetKey={open}
        />
      </div>
    </div>
  )
}

export default function CategoryLandingPagesPage() {
  const navigate = useNavigate()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedCreateCategory, setSelectedCreateCategory] = useState(CATEGORIES[0]?.code || '')
  const [selectedTemplateByCategory, setSelectedTemplateByCategory] = useState({})
  const [statusUpdatingId, setStatusUpdatingId] = useState(null)

  const hasPublishableTemplate = (templateId) => {
    if (!templateId) return false
    const normalized = String(templateId)
    if (normalized === 'default') return false
    return true
  }

  const showToast = (message, type = 'success') => {
    try { window?.toast?.show?.(message, type) } catch { }
  }

  const templatesQuery = useQuery({
    queryKey: ['category-landing-templates', 'all'],
    queryFn: async () => {
      const res = await api.get('/category-landing-templates', { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
  })

  const pagesQuery = useQuery({
    queryKey: ['category-landing-pages', 'me'],
    queryFn: async () => {
      const res = await api.get('/category-landing-pages/me', { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    }
  })

  const createMutation = useMutation({
    mutationFn: async ({ category, templateId }) => {
      const res = await api.post('/category-landing-pages/me', {
        category,
        enabled: false,
        templateId: templateId ? String(templateId) : 'default',
        formEnabled: true,
        surveyEnabled: false,
        kakaoNotificationEnabled: true,
      })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: (data) => {
      try { window?.toast?.show?.('홍보용페이지가 생성되었습니다', 'success') } catch { }
      navigate(`/category-landing-pages/${data.id}/edit`)
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '생성 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const updateEnabledMutation = useMutation({
    mutationFn: async ({ pageId, enabled }) => {
      const detailRes = await api.get(`/category-landing-pages/me/${pageId}`, { __silent: true })
      const detail = (detailRes?.data && detailRes.data.data !== undefined) ? detailRes.data.data : detailRes.data
      if (enabled && !hasPublishableTemplate(detail?.templateId)) {
        const err = new Error('공개하려면 먼저 템플릿을 적용해 주세요.')
        err.code = 'TEMPLATE_REQUIRED'
        throw err
      }
      const body = {
        enabled,
        templateId: detail?.templateId ? String(detail.templateId) : 'default',
        heroTitle: detail?.heroTitle || '',
        heroSubtitle: detail?.heroSubtitle || '',
        heroImageFileId: detail?.heroImageFileId || '',
        heroVideoFileId: detail?.heroVideoFileId || '',
        contentSections: JSON.stringify(detail?.contentSections || []),
        formEnabled: detail?.formEnabled !== false,
        formTitle: detail?.formTitle || '',
        formFields: JSON.stringify(detail?.formFields || []),
        surveyEnabled: detail?.surveyEnabled !== false,
        surveyCustomFields: JSON.stringify(detail?.surveyCustomFields || []),
        customConfig: JSON.stringify(detail?.customConfig || {}),
        adReviewNotice: typeof detail?.adReviewNotice === 'string' ? detail.adReviewNotice : '',
      }
      const res = await api.put(`/category-landing-pages/me/${pageId}`, body)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onMutate: ({ pageId }) => {
      setStatusUpdatingId(pageId)
    },
    onSuccess: (_, variables) => {
      showToast(variables?.enabled ? '페이지가 공개되었습니다.' : '페이지가 비공개로 전환되었습니다.', 'success')
      pagesQuery.refetch()
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '공개 상태 변경 실패'
      showToast(msg, 'error')
    },
    onSettled: () => {
      setStatusUpdatingId(null)
    },
  })

  if (pagesQuery.isLoading || templatesQuery.isLoading) return <Loading />
  if (pagesQuery.isError) return <InlineError message={pagesQuery.error?.message || '불러오기 실패'} />
  if (templatesQuery.isError) return <InlineError message={templatesQuery.error?.message || '템플릿 불러오기 실패'} />

  const pages = pagesQuery.data || []
  const templates = templatesQuery.data || []
  const pagesByCategory = {}
  pages.forEach(page => {
    if (!pagesByCategory[page.category]) {
      pagesByCategory[page.category] = []
    }
    pagesByCategory[page.category].push(page)
  })
  const templatesByCategory = {}
  templates.forEach((t) => {
    if (!templatesByCategory[t.category]) templatesByCategory[t.category] = []
    templatesByCategory[t.category].push(t)
  })
  const visibleCategories = CATEGORIES.filter((cat) => (pagesByCategory[cat.code] || []).length > 0)
  const createdCategoryCodes = CATEGORIES
    .filter((cat) => (pagesByCategory[cat.code] || []).length > 0)
    .map((cat) => cat.code)
  const creatableCategories = CATEGORIES.filter((cat) => !createdCategoryCodes.includes(cat.code))
  const hasCreatableCategory = creatableCategories.length > 0
  const createCategory = creatableCategories.find((cat) => cat.code === selectedCreateCategory) || creatableCategories[0] || CATEGORIES[0]
  const createCodeTemplateOptions = createCategory ? getCodeTemplateApplyOptions(createCategory.code) : []
  const createTemplateValue = createCategory
    ? selectedTemplateByCategory[createCategory.code] || createCodeTemplateOptions[0]?.value || 'default'
    : 'default'

  const createFlowProps = {
    categories: CATEGORIES,
    categoryCode: createCategory?.code || '',
    disabledCategoryCodes: createdCategoryCodes,
    templatesByCategory,
    templateValue: createTemplateValue,
    creating: createMutation.isPending || createMutation.isLoading,
    onCategoryChange: (code) => {
      if (createdCategoryCodes.includes(code)) return
      setSelectedCreateCategory(code)
    },
    onTemplateChange: (value) => {
      const code = createCategory?.code
      if (!code) return
      setSelectedTemplateByCategory((prev) => ({ ...prev, [code]: value }))
    },
    onCreate: () => {
      if (!hasCreatableCategory) return
      createMutation.mutate({ category: createCategory.code, templateId: createTemplateValue })
    },
  }

  const resolvePageUrl = (page) => {
    const path = page?.enabled ? `/c/${page.code}` : ''
    if (!path) return ''
    return typeof window !== 'undefined' ? `${window.location.origin}${path}` : path
  }

  const copyPageLink = async (page) => {
    const url = resolvePageUrl(page)
    if (!url) {
      showToast('공개된 페이지에서만 링크를 복사할 수 있습니다.', 'error')
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      showToast('공개 링크가 복사되었습니다.', 'success')
    } catch {
      showToast('링크 복사에 실패했습니다.', 'error')
    }
  }

  return (
    <div className="mx-auto max-w-5xl">
      <CategoryLandingCreateModal
        open={createModalOpen}
        {...createFlowProps}
        onClose={() => setCreateModalOpen(false)}
      />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold">홍보용페이지</h1>
            <MenuGuideButton slug="category-landing" title="홍보용페이지 가이드" />
          </div>
        </div>
        {visibleCategories.length > 0 ? (
          <button
            type="button"
            className="shrink-0 rounded-xl bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!hasCreatableCategory}
            onClick={() => {
              if (hasCreatableCategory) setCreateModalOpen(true)
            }}
          >
            {hasCreatableCategory ? '홍보용 페이지 만들기' : '모든 카테고리 생성됨'}
          </button>
        ) : null}
      </div>

      <div className="mt-6 space-y-4">
        {visibleCategories.length === 0 ? (
          <section className="mx-auto max-w-3xl">
            <CategoryLandingCreateContent
              {...createFlowProps}
              title="생성된 홍보용 페이지가 없습니다. 만들어주세요."
              description="카테고리와 템플릿을 고르면 기본 구성이 먼저 만들어지고, 이후 세부 편집을 이어갈 수 있습니다."
              showCloseButton={false}
              showCancelButton={false}
              className="shadow-sm"
            />
          </section>
        ) : (
          <h2 className="text-lg font-bold text-slate-900">생성된 홍보용 페이지</h2>
        )}

        {visibleCategories.map(cat => {
          const categoryPages = pagesByCategory[cat.code] || []

          return (
            <div
              key={cat.code}
              className="bg-white"
            >
              <div className="space-y-2">
                {categoryPages.map(page => (
                  <div key={page.id} className="flex flex-col gap-3 rounded-xl border border-slate-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="shrink-0 text-base font-semibold text-slate-900">{cat.label}</span>
                      <span className="hidden text-slate-300 lg:inline">|</span>
                      <span className="min-w-0 truncate text-sm font-medium text-slate-700">{page.heroTitle || '제목 없음'}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
                      <select
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700"
                        value={page.enabled ? 'enabled' : 'disabled'}
                        disabled={statusUpdatingId === page.id}
                        onChange={(e) => {
                          const nextEnabled = e.target.value === 'enabled'
                          if (nextEnabled === Boolean(page.enabled)) return
                          updateEnabledMutation.mutate({ pageId: page.id, enabled: nextEnabled })
                        }}
                      >
                        <option value="disabled">비공개</option>
                        <option value="enabled">공개</option>
                      </select>
                      <button
                        type="button"
                        className={`rounded-lg border border-slate-200 px-3 py-1.5 text-sm ${page.enabled ? 'text-slate-700 hover:bg-slate-50' : 'cursor-not-allowed text-slate-300'}`}
                        onClick={() => copyPageLink(page)}
                        disabled={!page.enabled}
                      >
                        링크복사
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => navigate(`/category-landing-pages/${page.id}/edit?reconfigure=1`)}
                      >
                        다시 구성하기
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => navigate(`/category-landing-pages/${page.id}/edit`)}
                      >
                        수정
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
